// Main Flash Report Dashboard Component
// Shows all plants horizontally with current week and 13-week average data
// ✅ FIXED: section-start class added to first metric of each group
//           This replaces fragile nth-child row counting in CSS
// ✅ FIXED: row-odd / row-even classes added to drive alternating row colors
//           nth-child(odd/even) cannot be used because hidden spacer rows
//           still count in the DOM and throw the pattern off

import React, { useState, useEffect } from 'react';
import { flashReportAPI } from '../utils/api';
import { formatDateForInput, getMostRecentSaturday, isSaturday } from '../utils/dateHelpers';
import '../styles/MainDashboard.css';

function MainDashboard() {
    const [weekEnding, setWeekEnding] = useState('');
    const [dashboardData, setDashboardData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load dashboard data when component mounts
    useEffect(() => {
        const defaultDate = formatDateForInput(getMostRecentSaturday());
        setWeekEnding(defaultDate);
        loadDashboard(defaultDate);
    }, []);

    // Handle week ending date change with Saturday validation
    const handleWeekEndingChange = (e) => {
        const selectedDate = e.target.value;
        if (!isSaturday(selectedDate)) {
            setMessage({ text: 'Week ending must be a Saturday. Please select a Saturday.', type: 'error' });
            return;
        }
        setWeekEnding(selectedDate);
        setMessage({ text: '', type: '' });
        loadDashboard(selectedDate);
    };

    // Load dashboard data for all plants
    const loadDashboard = async (date) => {
        try {
            setLoading(true);
            setMessage({ text: '', type: '' });
            const data = await flashReportAPI.getDashboard(date);
            if (data.plants && data.plants.length > 0) {
                setDashboardData(data.plants);
            } else {
                setDashboardData([]);
                setMessage({ text: 'No data available for this week', type: 'info' });
            }
        } catch (error) {
            setMessage({ text: 'Error loading dashboard data', type: 'error' });
            setDashboardData([]);
        } finally {
            setLoading(false);
        }
    };

    // Format number with commas and show "-" for zeros/nulls
    const formatNumber = (num, decimals = 0) => {
        if (num === null || num === undefined || num === '' || isNaN(num)) return '-';
        const numValue = parseFloat(num);
        if (Math.abs(numValue) < 0.0001) return '-';
        return numValue.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Handle Excel export
    const handleExportExcel = async () => {
        if (!weekEnding || dashboardData.length === 0) {
            setMessage({ text: 'No data to export', type: 'error' });
            return;
        }
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/export/flash-dashboard/${weekEnding}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            if (!response.ok) throw new Error('Export failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Flash_Dashboard_${weekEnding}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setMessage({ text: 'Dashboard exported successfully', type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            setMessage({ text: 'Error exporting dashboard', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // ── PM completion cell ──────────────────────────────────────────────────
    // Percentage only. The raw "33 of 38" counts were shown here initially and
    // removed at Jay's request 2026-08-12 — this screen carries 9 plants × 2
    // columns and the extra line made it noisy. The counts are still available
    // on the Flash 13 Week View, which is the drill-down for a single plant,
    // and they remain stored in flash_report_data either way.
    //
    // Two things this deliberately does NOT do:
    //   * it does not use formatNumber, which returns '-' for anything below
    //     0.0001 — that would turn a real 0% (nothing completed) into a dash,
    //     which is the one reading we must never show for a plant that did
    //     raise tickets;
    //   * it does not treat NULL as zero. NULL means the plant created no
    //     maintenance tickets that week, so it has no completion rate at all.
    //     San Antonio and Spohn are NULL most weeks; printing 0% would report
    //     them as having completed nothing.
    const renderPmCell = (metrics) => {
        const total = metrics?.pm_tickets_total;
        const done  = metrics?.pm_tickets_completed;

        // No data: no tickets raised, or no figure stored for this week yet.
        if (total === null || total === undefined || total === 0) {
            return <span className="pm-nodata" title="No maintenance tickets raised this week">-</span>;
        }

        const pct = (done / total) * 100;
        // title= keeps the underlying counts reachable on hover without
        // putting a second line back into the cell.
        return <span className="pm-pct" title={`${done} of ${total} completed`}>{pct.toFixed(1)}%</span>;
    };

    // Format date for display (e.g. 3/7/2026)
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const parts = dateString.split('-');
        const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
    };

    // ✅ Metric groups — first metric in each group gets section-start=true
    //    This drives the left-border accent styling in CSS without relying
    //    on fragile nth-child row counting (which breaks when spacer rows exist)
    //
    // ⚠️  REVENUE METRICS COMMENTED OUT (April 2026)
    //     Revenue, Revenue/Lb., and % of Sales have been hidden from the
    //     display at the request of management. The data is still saved to
    //     the database and can be restored by uncommenting the lines below.
    const metricGroups = [
        {
            color: 'blue',
            metrics: [
                { key: 'clean_lbs', label: 'Clean LBS', decimals: 0, direction: 'higher' },
                { key: 'soil_lbs',  label: 'Soil LBS',  decimals: 0, direction: 'higher' },
                // { key: 'revenue', label: 'Revenue', decimals: 0 },  // ⚠️ Hidden — uncomment to restore
            ]
        },
        {
            color: 'yellow',
            metrics: [
                // { key: 'revenue_per_lb', label: 'Revenue/Lb.', decimals: 4 },  // ⚠️ Hidden — uncomment to restore
                { key: 'direct_hours', label: 'Direct Hrs.', decimals: 0, direction: 'lower' },
                { key: 'ppoh',         label: 'PPOH',        decimals: 2, direction: 'higher' },
                { key: 'direct_labor', label: 'Direct $',    decimals: 0, direction: 'lower' }
            ]
        },
        {
            color: 'green',
            metrics: [
                { key: 'direct_dollar_per_lb', label: 'Direct $/Lb.', decimals: 4, direction: 'lower' }
            ]
        },
        {
            color: 'orange',
            metrics: [
                { key: 'total_hours', label: 'Total Hrs.',    decimals: 0, direction: 'lower' },
                { key: 'total_ppoh',  label: 'Total PPOH',   decimals: 2, direction: 'higher' },
                { key: 'total_labor', label: 'Total Labor $', decimals: 0, direction: 'lower' }
            ]
        },
        {
            color: 'purple',
            metrics: [
                { key: 'total_ot_hours',     label: 'Total OT',          decimals: 0, direction: 'lower' },
                { key: 'ot_percentage',      label: 'OT %',              decimals: 1, direction: 'lower' },
                { key: 'total_labor_per_lb', label: 'Total Labor $/Lb.', decimals: 4, direction: 'lower' },
                // { key: 'percent_of_sales', label: '% of Sales', decimals: 2 },  // ⚠️ Hidden — uncomment to restore
            ]
        },
        {
            color: 'teal',
            metrics: [
                { key: 'packroom_fte',   label: 'Packroom FTE',    decimals: 0, direction: 'lower' },
                { key: 'linen_dist_fte', label: 'Linen Dist. FTE', decimals: 0, direction: 'lower' }
            ]
        },
        {
            // PM completion from FMX. `render` overrides the default number
            // formatter because this cell is not a plain number — it must show
            // the raw counts next to the percentage. Some plant-weeks have a
            // denominator of 1 or 2, where a bare percentage is meaningless
            // and one ticket moves it 100 points.
            color: 'blue',
            metrics: [
                { key: 'pm_completion_pct', label: 'PM Complete', decimals: 1, direction: 'higher', render: renderPmCell }
            ]
        },
        // ── Utilities Per Pound (Jay, 2026-09-30) ──────────────────────────
        // Water gallons / kWh / therms per CLEAN lb, from each plant's meter
        // database. Shown per plant behind the switch on the Utilities Per Pound
        // page (user menu): ON -> everyone sees it; OFF -> only admins see it,
        // in italics, as a preview. The API decides who sees what
        // (utilities_shown / utilities_preview) and sends null otherwise, so
        // this screen just renders. The whole group is left out when no plant
        // is shown to this viewer. Lower is better, so a week above its 13-week
        // average goes red.
        ...(dashboardData.some(p => p.utilities_shown) ? [{
            color: 'green',
            metrics: [
                { key: 'water_per_lb',  label: 'Water/lb',  decimals: 3, direction: 'lower', utility: true },
                { key: 'kwh_per_lb',    label: 'KWH/lb',    decimals: 4, direction: 'lower', utility: true },
                { key: 'therms_per_lb', label: 'Therms/lb', decimals: 4, direction: 'lower', utility: true }
            ]
        }] : [])
    ];

    // True when at least one plant's utility figures are an admin-only preview
    const hasUtilityPreview = dashboardData.some(p => p.utilities_preview);

    // Plants whose current week was scaled up from missing meter readings.
    // The API sends utilities_partial_days to admins only (null for everyone
    // else), so only admins ever see this note.
    const partialPlants = dashboardData.filter(p => p.utilities_partial_days !== null && p.utilities_partial_days !== undefined);

    // ✅ Build a flat list of all visible metric rows so we can number them
    //    and assign row-odd / row-even correctly — spacer rows are excluded
    //    so the alternating pattern is always correct regardless of grouping
    const allMetricRows = metricGroups.flatMap(group =>
        group.metrics.map(metric => ({ ...metric, color: group.color }))
    );

    return (
        <div className="main-dashboard-container">
            <div className="dashboard-header-section">
                <div className="header-content">
                    <h2>Flash Report</h2>
                    <p className="subtitle">Week ending {formatDate(weekEnding)}</p>
                </div>

                <div className="header-controls">
                    <div className="report-actions">
                        <button
                            onClick={() => window.print()}
                            className="btn-print"
                            disabled={loading || dashboardData.length === 0}
                        >
                            🖨️ Print
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="btn-export"
                            disabled={loading || dashboardData.length === 0}
                        >
                            📊 Export to Excel
                        </button>
                    </div>

                    <div className="date-selector">
                        <input
                            type="date"
                            id="dashboardWeekEnding"
                            value={weekEnding}
                            onChange={handleWeekEndingChange}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Admins only ever get preview figures, so only they see this note */}
            {!loading && hasUtilityPreview && (
                <div className="utility-preview-note">
                    Utilities Per Pound figures in <em>italics</em> are an admin preview: only admins
                    can see them. To show a plant to everyone, switch it ON under
                    user menu → Utilities Per Pound.
                </div>
            )}

            {/* Admins only: a week scaled up from missing meter readings */}
            {!loading && partialPlants.length > 0 && (
                <div className="utility-preview-note">
                    Utilities Per Pound estimated from missing meter readings, scaled to a full week:{' '}
                    {partialPlants.map(p => `${p.plant_name} ${p.utilities_partial_days} of 7 days`).join(', ')}.
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            ) : dashboardData.length > 0 ? (
                <div className="dashboard-table-wrapper">
                    <table className="dashboard-table">
                        <thead>
                            {/* Row 1: Plant name headers */}
                            <tr>
                                <th className="metric-header"></th>
                                {dashboardData.map(plant => (
                                    <th key={plant.plant_id} colSpan="2" className="plant-header">
                                        {plant.plant_name}
                                    </th>
                                ))}
                            </tr>
                            {/* Row 2: Current / Last 13 WK Avg sub-headers */}
                            <tr>
                                <th className="metric-header"></th>
                                {dashboardData.map(plant => (
                                    <React.Fragment key={plant.plant_id}>
                                        <th className="sub-header">
                                            Current<br />{formatDate(weekEnding)}
                                        </th>
                                        <th className="sub-header avg-col">
                                            Last 13 WK<br />Avg
                                        </th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {metricGroups.map((group, groupIndex) => (
                                <React.Fragment key={groupIndex}>
                                    {group.metrics.map((metric, metricIndex) => {

                                        // ✅ Find this metric's position in the flat list
                                        //    to get a reliable odd/even number that ignores
                                        //    hidden spacer rows entirely
                                        const rowNumber = allMetricRows.findIndex(r => r.key === metric.key);
                                        const rowStripe = rowNumber % 2 === 0 ? 'row-odd' : 'row-even';

                                        return (
                                            <tr
                                                key={metric.key}
                                                className={[
                                                    `color-${group.color}`,
                                                    rowStripe,
                                                    metricIndex === 0 && groupIndex > 0 ? 'section-start' : ''
                                                ].filter(Boolean).join(' ')}
                                            >
                                                <td className="metric-label">{metric.label}</td>
                                                {dashboardData.map(plant => {
                                                    const curVal = parseFloat(plant.current_week?.[metric.key]);
                                                    const avgVal = parseFloat(plant.avg_13_week?.[metric.key]);
                                                    const hasData = !isNaN(curVal) && curVal > 0.0001 && !isNaN(avgVal) && avgVal > 0.0001;
                                                    const isUnderperforming = hasData && metric.direction &&
                                                        (metric.direction === 'higher' ? curVal < avgVal : curVal > avgVal);
                                                    // Utility figures this viewer sees only because they are an admin
                                                    const isPreview = metric.utility && plant.utilities_preview;
                                                    const previewTitle = isPreview
                                                        ? `Preview: only admins see this until ${plant.plant_name} is switched ON`
                                                        : undefined;
                                                    return (
                                                        <React.Fragment key={plant.plant_id}>
                                                            <td
                                                                className={`data-cell${isUnderperforming ? ' cell-bad' : ''}${isPreview ? ' cell-preview' : ''}`}
                                                                title={previewTitle}
                                                            >
                                                                {metric.render
                                                                    ? metric.render(plant.current_week)
                                                                    : formatNumber(plant.current_week?.[metric.key], metric.decimals)}
                                                            </td>
                                                            <td
                                                                className={`data-cell avg-col${isPreview ? ' cell-preview' : ''}`}
                                                                title={previewTitle}
                                                            >
                                                                {metric.render
                                                                    ? metric.render(plant.avg_13_week)
                                                                    : formatNumber(plant.avg_13_week?.[metric.key], metric.decimals)}
                                                            </td>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                    {/* Spacer between groups - hidden via CSS but kept for structure */}
                                    {groupIndex < metricGroups.length - 1 && (
                                        <tr className="spacer-row">
                                            <td colSpan="100%"></td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No data available. Please enter data for plants in the Data Entry tab.</p>
                </div>
            )}
        </div>
    );
}

export default MainDashboard;